import { Coins, Search, BookOpen, Puzzle, Wrench, Globe } from "lucide-react";

const Section = ({ emoji, title, subtitle, children }) => (
  <div className="mt-8 first:mt-0">
    <h4 className="font-heading font-bold text-lg sm:text-xl text-white flex items-center gap-2.5">
      <span className="text-xl leading-none">{emoji}</span>
      <span className="text-gradient-accent">{title}</span>
    </h4>
    {subtitle && (
      <p className="mt-2 font-mono text-xs uppercase tracking-[0.18em] text-cyan-400/80">
        {subtitle}
      </p>
    )}
    <div className="mt-3 space-y-3 text-sm sm:text-[15px] text-slate-300 leading-relaxed">
      {children}
    </div>
  </div>
);

const configOptions = [
  "Token Name",
  "Token Symbol",
  "Total Supply",
  "Decimals",
  "Ownership settings",
  "Mint / Burn functionality",
  "Additional token features",
];

const ozQualities = [
  { icon: Search, label: "Transparent" },
  { icon: BookOpen, label: "Open source" },
  { icon: Puzzle, label: "Standardized" },
  { icon: Wrench, label: "Designed for verification" },
  { icon: Globe, label: "EVM compatible" },
];

const steps = [
  { t: "Choose your network", d: "Select a supported EVM blockchain." },
  { t: "Configure your token", d: "Enter your token information and select available features." },
  { t: "Review your configuration", d: "Verify your settings before deployment." },
  { t: "Pay the creation fee", d: "Complete the applicable Autono Dex fee and network transaction." },
  { t: "Deploy your token", d: "Your token contract is deployed directly to the selected blockchain." },
  { t: "Verify your contract", d: "Publish and verify your source code through the appropriate blockchain explorer." },
];

const fees = [
  { label: "Standard Token", desc: "Base token creation fee." },
  { label: "Custom Token", desc: "Base creation fee + applicable customization fees." },
  { label: "Network", desc: "Applicable blockchain transaction / gas fees may also apply." },
];

export const TokenDetail = () => {
  return (
    <div data-testid="token-detail-content">
      <Section emoji="🪙" title="Token Generator" subtitle="Create. Customize. Deploy.">
        <p>
          Autono Dex makes it easy to create and deploy your own token across supported EVM networks
          — without needing to build a smart contract from scratch.
        </p>
        <p>
          Our Token Generator is designed to give creators a streamlined way to launch their own
          tokens while maintaining transparency through standardized, open-source smart-contract
          code.
        </p>
      </Section>

      <Section emoji="🌐" title="Deploy Across Supported EVM Networks">
        <p>Choose a supported EVM network and create your token directly through Autono Dex.</p>
        <p>
          As our supported network ecosystem expands, creators will be able to deploy tokens across
          multiple EVM-compatible blockchains from one platform.
        </p>
      </Section>

      <Section emoji="⚙️" title="Customize Your Token">
        <p>Every project can have its own configuration and customization.</p>
        <p>
          Depending on the token package you select, you may be able to configure options such as:
        </p>
        <div className="flex flex-wrap gap-2 pt-1">
          {configOptions.map((o) => (
            <span
              key={o}
              className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-slate-200"
            >
              {o}
            </span>
          ))}
        </div>
        <p className="text-slate-400">
          Standard token creation provides the core functionality, while custom token configurations
          may be available for creators who need additional functionality.
        </p>
        <p className="text-xs text-slate-500">Additional fees may apply to custom token creation.</p>
      </Section>

      <Section emoji="🔓" title="OpenZeppelin-Based Contracts">
        <p>
          Autono Dex tokens will use OpenZeppelin-based smart-contract standards designed around
          widely used, established Solidity libraries and patterns.
        </p>
        <p>Our goal is to provide creators with token contracts that are:</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-1">
          {ozQualities.map((q) => {
            const Icon = q.icon;
            return (
              <div
                key={q.label}
                className="flex items-center gap-2 rounded-lg border border-white/10 bg-black/40 px-3 py-2.5"
              >
                <Icon className="w-4 h-4 text-cyan-400 shrink-0" />
                <span className="text-xs text-slate-200">{q.label}</span>
              </div>
            );
          })}
        </div>
        <p className="text-slate-400">
          The source code for generated contracts will be available for creators to review.
        </p>
      </Section>

      <Section emoji="🔎" title="Contract Verification">
        <p>
          You are responsible for publishing and verifying your token's source code after
          deployment.
        </p>
        <p>
          Autono Dex provides the token-generation infrastructure, but verification on the
          appropriate blockchain explorer remains the creator's responsibility. This allows the
          creator to maintain control over their deployed contract and its published source code.
        </p>
        <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-4">
          <p className="font-heading font-bold text-white text-sm">Need Help?</p>
          <p className="mt-1 text-sm text-slate-300">
            If you're unsure how to publish or verify your contract, reach out to the Autono Dex team
            and we'll be happy to assist with the process.
          </p>
        </div>
      </Section>

      <Section emoji="💰" title="Token Creation Fees">
        <p>Creating a token through Autono Dex will require a token creation fee.</p>
        <p>The fee structure may vary depending on:</p>
        <div className="grid gap-3 pt-1">
          {fees.map((f) => (
            <div key={f.label} className="rounded-xl border border-white/10 bg-black/40 p-4">
              <p className="font-heading font-bold text-white text-sm">{f.label}</p>
              <p className="mt-1 text-sm text-slate-400">{f.desc}</p>
            </div>
          ))}
        </div>
        <p className="text-slate-400">
          All applicable fees should be displayed before you finalize your token deployment.
        </p>
      </Section>

      <Section emoji="🚀" title="From Idea to Deployment">
        <p>Autono Dex is designed to simplify the process:</p>
        <div className="space-y-3 pt-1">
          {steps.map((s, i) => (
            <div key={s.t} className="flex items-start gap-3">
              <span className="shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-cyan-500/30 to-purple-600/30 border border-white/10 flex items-center justify-center font-mono text-xs font-bold text-cyan-300">
                {i + 1}
              </span>
              <div>
                <p className="font-semibold text-white text-sm">{s.t}</p>
                <p className="text-sm text-slate-400">{s.d}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <div className="mt-8 rounded-xl border border-cyan-500/20 bg-gradient-to-br from-cyan-500/5 to-purple-600/5 p-5 flex items-start gap-3">
        <Coins className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-white text-sm">
            Create your token. Deploy it on-chain. Own your project.
          </p>
          <p className="mt-1 font-heading font-bold text-white">Autono Dex</p>
          <p className="text-sm text-gradient-accent font-semibold">
            Autonomous Intelligence. Decentralized Finance.
          </p>
        </div>
      </div>
    </div>
  );
};
